// frontend/src/pages/StudentTopicFeed.js — NEW FILE (Phase B)
// Perplexity-Discover-style mobile feed for one topic.
// - Chips row (All + section names from parent_heading)
// - Tall image cards / compact accent cards (no image) / gradient hero fallback
// - Expand-in-place: summary + 3 key points + reading time + "Study this →"
// - "Study this" opens StudentArticleReader full-page
// - Hardware/gesture back closes reader first (pushState), feed scroll restored
// - Skeleton shimmer while loading

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE, authFetch } from '../services/api.js';
import StudentArticleReader from './StudentArticleReader';
import { resolveImageUrl, stripHtml } from './ContentBlockRenderer';
import './studentfeed.css';
import { useLanguage } from '../contexts/languagecontext';

import { getStudentId } from './AskAiPanel';
import { FLAG_META } from './ProgressWidgets';

// Subject-flavoured gradient pairs for cards without images
const GRADIENTS = [
    ['#4f46e5', '#7c3aed'], // indigo → violet
    ['#0e7490', '#059669'], // cyan → emerald
    ['#b45309', '#dc2626'], // amber → red
    ['#be185d', '#7c3aed'], // pink → violet
    ['#1d4ed8', '#0e7490'], // blue → cyan
];

const gradientFor = (text) => {
    let h = 0;
    const s = text || '';
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 997;
    return GRADIENTS[h % GRADIENTS.length];
};

const LABELS = {
    en: { studyThis: 'Study this', minRead: 'min read', keyPoints: 'Key points', empty: 'No content available yet.', all: 'All', retry: 'Retry', loadFail: 'Could not load content. Check your connection and try again.' },
    hi: { studyThis: 'अध्ययन करें', minRead: 'min read', keyPoints: 'मुख्य बिंदु', empty: 'अभी कोई सामग्री उपलब्ध नहीं है।', all: 'All', retry: 'फिर कोशिश करें', loadFail: 'सामग्री लोड नहीं हो सकी। इंटरनेट जांच कर दोबारा कोशिश करें।' },
};

const StudentTopicFeed = ({ topicId, topicName, language = 'en', onBack }) => {
    const t = LABELS[language] || LABELS.en;

    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedIds, setExpandedIds] = useState({});      // { `${card_type}-${card_id}`: true }
    const [activeChip, setActiveChip] = useState('__all__');
    const [readerIndex, setReaderIndex] = useState(null);    // index into filtered list
    const savedScroll = useRef(0);
    const cardRefs = useRef({});
    const { hideImages, toggleImages } = useLanguage();

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/content-page/topic/${topicId}/flattened?language=${language}`);
            if (!res.ok) throw new Error('load-failed');
            const data = await res.json();
            setCards(data.filter((c) => c.card_type === 'subtopic'));
        } catch (err) {
            setError(t.loadFail);
        } finally {
            setLoading(false);
        }
    }, [topicId, language]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);
    // ── Progress map: {"subtopic-281": {status, confidence, flag}} ──
    const [progress, setProgress] = useState({});
    const loadProgress = useCallback(async () => {
        const sid = getStudentId();
        if (!sid) return;
        try {
            const res = await authFetch(`${API_BASE}/progress/topic/${topicId}?student_id=${sid}`);
            if (res.ok) setProgress(await res.json());
        } catch { /* progress is optional */ }
    }, [topicId]);
    useEffect(() => { loadProgress(); }, [loadProgress]);

    const firstUnfinished = cards.findIndex((c) => progress[`${c.card_type}-${c.card_id}`]?.status !== 'completed');
    const showResume = Object.keys(progress).length > 0 && firstUnfinished > 0;
    const revisionCount = cards.filter((c) => {
        const p = progress[`${c.card_type}-${c.card_id}`];
        return p && (p.flag || p.confidence === 'low');
    }).length;

    // ── Chips: unique section names (parent_heading), only when meaningful ──
    const sections = [];
    cards.forEach((c) => {
        if (c.parent_heading && !sections.includes(c.parent_heading)) sections.push(c.parent_heading);
    });
    const showChips = sections.length > 1;
    const filtered = activeChip === '__all__' ? cards
        : activeChip === '__revision__' ? cards.filter((c) => {
            const p = progress[`${c.card_type}-${c.card_id}`];
            return p && (p.flag || p.confidence === 'low');
        })
        : cards.filter((c) => c.parent_heading === activeChip);

    // ── Reader open/close with hardware-back support ──
    const openReader = (index) => {
        savedScroll.current = window.scrollY;
        window.history.pushState({ stfReader: true }, '');
        setReaderIndex(index);
    };
    const closeReader = useCallback(() => {
        setReaderIndex(null);
        loadProgress();
        requestAnimationFrame(() => window.scrollTo(0, savedScroll.current));
    }, [loadProgress]);

    if (readerIndex !== null) {
        return (
            <StudentArticleReader
                topicId={topicId}
                card={filtered[readerIndex]}
                language={language}
                topicName={topicName}
                onClose={closeReader}
            />
        );
    }

    return (
        <div className="stf-container">
            <div className="stf-header">
                {onBack && <button className="stf-back-btn" onClick={onBack} aria-label="Back">&larr;</button>}
                <h2>{topicName}</h2>
                <button
                    className="stf-imgtoggle-btn"
                    onClick={toggleImages}
                    title={hideImages ? 'Show images' : 'Hide images'}
                    aria-label={hideImages ? 'Show images' : 'Hide images'}
                >
                    {hideImages ? '🖼️' : '🚫'}
                </button>
            </div>

            {(showChips || revisionCount > 0) && (
                <div className="stf-chips" role="tablist">
                    <button
                        className={`stf-chip ${activeChip === '__all__' ? 'stf-chip-active' : ''}`}
                        onClick={() => setActiveChip('__all__')}
                    >{t.all}</button>
                    {sections.map((s) => (
                        <button
                            key={s}
                            className={`stf-chip ${activeChip === s ? 'stf-chip-active' : ''}`}
                            onClick={() => setActiveChip(s)}
                        >{s}</button>
                    ))}
                    {revisionCount > 0 && (
                        <button
                            className={`stf-chip ${activeChip === '__revision__' ? 'stf-chip-active' : ''}`}
                            onClick={() => setActiveChip('__revision__')}
                        >⭐ {language === 'hi' ? 'दोहराना' : 'Revision'} ({revisionCount})</button>
                    )}
                </div>
            )}

            {error && (
                <div className="stf-error">
                    <p>{error}</p>
                    <button onClick={load}>{t.retry}</button>
                </div>
            )}

            {loading ? (
                <div className="stf-feed">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className="stf-skeleton">
                            {i % 2 === 0 && <div className="stf-skel-img" />}
                            <div className="stf-skel-line stf-skel-line-sm" />
                            <div className="stf-skel-line" />
                            <div className="stf-skel-line stf-skel-line-lg" />
                        </div>
                    ))}
                </div>
            ) : (
                <div className="stf-feed">
                    {showResume && activeChip === '__all__' && (
                        <button className="stf-resume" onClick={() => openReader(firstUnfinished)}>
                            ▶ {language === 'hi' ? 'जहाँ छोड़ा था वहाँ से जारी रखें' : 'Continue where you left off'}
                        </button>
                    )}
                    {filtered.map((card, idx) => {
                        const id = `${card.card_type}-${card.card_id}`;
                        const isOpen = !!expandedIds[id];
                        const heading = stripHtml(card.heading) || '(untitled)';
                        const hasImage = !!card.feature_image_url;
                        const [g1, g2] = gradientFor(card.parent_heading || topicName);
                        const isHero = idx === 0 && !hasImage;

                        return (
                            <article
                                key={id}
                                ref={(el) => { cardRefs.current[id] = el; }}
                                className={`stf-card ${hasImage ? 'stf-card-image' : (isHero ? 'stf-card-hero' : 'stf-card-compact')} ${isOpen ? 'stf-card-open' : ''}`}
                                style={!hasImage ? { '--stf-g1': g1, '--stf-g2': g2 } : undefined}
                            >
                                <div
                                    className="stf-card-head"
                                    onClick={() => setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }))}
                                >
                                    {hasImage && (
                                        <img src={resolveImageUrl(card.feature_image_url)} alt="" className="stf-card-img" loading="lazy" />
                                    )}
                                    {isHero && (
                                        <div className="stf-hero-art">
                                            <span className="stf-hero-title">{heading}</span>
                                        </div>
                                    )}
                                    {card.parent_heading && <span className="stf-eyebrow">{card.parent_heading}</span>}
                                    {!isHero && <h3 className="stf-heading">{heading}</h3>}
                                    {card.short_summary && (
                                        <p className={`stf-snippet ${isOpen ? 'stf-snippet-open' : ''}`}>{card.short_summary}</p>
                                    )}
                                    <div className="stf-meta">
                                        {card.reading_time_min ? <span>{card.reading_time_min} {t.minRead}</span> : null}
                                        {progress[id]?.status === 'completed' && <span className="stf-done">✓ {language === 'hi' ? 'पूर्ण' : 'Done'}</span>}
                                        {progress[id]?.status === 'viewed' && <span className="stf-inprogress">{language === 'hi' ? 'जारी' : 'In progress'}</span>}
                                        {progress[id]?.flag && <span>{FLAG_META[progress[id].flag]?.icon}</span>}
                                        {progress[id]?.confidence === 'low' && <span>😕</span>}
                                    </div>
                                </div>

                                {isOpen && (
                                    <div className="stf-card-more">
                                        {Array.isArray(card.key_points) && card.key_points.length > 0 && (
                                            <ul className="stf-keypoints">
                                                {card.key_points.slice(0, 3).map((p, i) => (
                                                    <li key={i}>{p}</li>
                                                ))}
                                            </ul>
                                        )}
                                        <button className="stf-study-btn" onClick={() => openReader(idx)}>
                                            {t.studyThis} &rarr;
                                        </button>
                                    </div>
                                )}
                            </article>
                        );
                    })}
                    {filtered.length === 0 && !error && <p className="stf-empty">{t.empty}</p>}
                </div>
            )}
        </div>
    );
};

export default StudentTopicFeed;